import { supabase } from '../supabaseClient';
import { formatCurrency } from '../utils/currency';

// Order service for handling portal orders only
export const orderService = {
  
  // Get all orders for a customer (portal orders only)
  async getAllOrders(options = {}) {
    const {
      outstandingOnly = false,
      includeLineItems = false,
      limit = 50,
      orderBy = 'created_at',
      orderDirection = 'desc'
    } = options;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch portal orders only
      const portalOrders = await this.getPortalOrders({
        outstandingOnly,
        limit,
        orderBy,
        orderDirection
      });

      // Normalize portal orders
      const normalizedOrders = portalOrders.map(order => ({
        ...order,
        source: 'portal',
        order_type: 'portal_order',
        order_number: order.order_number || order.order_id,
        order_date: order.created_at,
        raw_status: order.status,
        status: this.normalizePortalStatus(order.status),
        order_total_inc_tax: this.calculatePortalOrderTotal(order.cart_items),
        finalised: order.status === 'Complete'
      }));

      return {
        orders: normalizedOrders,
        total: normalizedOrders.length,
        portalCount: normalizedOrders.length
      };

    } catch (error) {
      console.error('Error fetching portal orders:', error);
      throw error;
    }
  },


  // Get portal orders from existing Supabase orders table
  async getPortalOrders(options = {}) {
    const {
      outstandingOnly = false,
      limit = 50,
      orderBy = 'created_at',
      orderDirection = 'desc'
    } = options;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = supabase
        .from('orders')
        .select(`
          *,
          delivery_addresses!delivery_address_id (
            id,
            address_line1,
            address_line2,
            city,
            state,
            postal_code,
            country,
            is_primary
          )
        `)
        .eq('user_id', user.id);

      // Filter for outstanding orders
      if (outstandingOnly) {
        query = query.not('status', 'in', '("Complete","Cancelled")');
      }

      // Apply ordering and limit
      query = query
        .order(orderBy, { ascending: orderDirection === 'asc' })
        .limit(limit);

      const { data, error } = await query;

      if (error) throw error;

      return data || [];

    } catch (error) {
      console.error('Error fetching portal orders:', error);
      throw error;
    }
  },

  // Get outstanding orders summary for dashboard (portal orders only)
  async getOutstandingOrdersSummary() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get portal orders summary
      const { data: portalOrders, error: portalError } = await supabase
        .from('orders')
        .select('status, cart_items, created_at')
        .eq('user_id', user.id);

      if (portalError) throw portalError;

      // Calculate outstanding orders (not Complete or Cancelled)
      const outstandingOrders = portalOrders?.filter(order => 
        !['Complete', 'Cancelled'].includes(order.status)
      ) || [];
      
      const allOrdersCount = portalOrders?.length || 0;
      const outstandingCount = outstandingOrders.length;
      const outstandingValue = outstandingOrders.reduce((sum, order) => {
        return sum + this.calculatePortalOrderTotal(order.cart_items);
      }, 0);

      // Find latest order date
      const latestOrderDate = portalOrders?.length > 0 
        ? portalOrders.reduce((latest, order) => {
            const orderDate = new Date(order.created_at);
            return orderDate > new Date(latest) ? order.created_at : latest;
          }, portalOrders[0].created_at)
        : null;

      return {
        total_outstanding: outstandingCount,
        total_outstanding_value: outstandingValue,
        portal_outstanding: outstandingCount,
        portal_value: outstandingValue,
        total_orders: allOrdersCount,
        latest_order_date: latestOrderDate
      };

    } catch (error) {
      console.error('Error fetching outstanding orders summary:', error);
      throw error;
    }
  },

  // Get specific order details with line items (portal orders only)
  async getOrderDetails(orderNumber) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          delivery_addresses!delivery_address_id (
            id,
            address_line1,
            address_line2,
            city,
            state,
            postal_code,
            country,
            is_primary
          )
        `)
        .eq('user_id', user.id)
        .eq('order_number', orderNumber)
        .single();

      if (error) throw error;
      return { ...data, source: 'portal' };

    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  },

  // Helper functions
  normalizePortalStatus(status) {
    const statusMap = {
      'Pending': '2',
      'In Progress': '2', 
      'Complete': 'c',
      'Cancelled': 'x',
      'On Hold': 'h'
    };
    return statusMap[status] || '2';
  },

  calculatePortalOrderTotal(cartItems) {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
    }, 0);
  },

  getStatusDisplayName(status) {
    const statusMap = {
      '2': 'In Progress',
      'c': 'Complete',
      'C': 'Complete', 
      'h': 'On Hold',
      'H': 'On Hold',
      'x': 'Cancelled',
      'X': 'Cancelled'
    };
    return statusMap[status] || status;
  },

  getStatusColor(status) {
    const colorMap = {
      '2': 'bg-blue-100 text-blue-800 border-blue-200',
      'c': 'bg-green-100 text-green-800 border-green-200',
      'C': 'bg-green-100 text-green-800 border-green-200',
      'h': 'bg-orange-100 text-orange-800 border-orange-200',
      'H': 'bg-orange-100 text-orange-800 border-orange-200',
      'x': 'bg-red-100 text-red-800 border-red-200',
      'X': 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  },

  // Format currency values
  formatCurrency(amount, currency = 'AUD') {
    return formatCurrency(amount, currency);
  },

  // Submitted but not yet approved by MHS staff. Works on raw rows and on
  // getAllOrders() rows, where the original status is kept as raw_status.
  isAwaitingApproval(order) {
    return order.internal_status === 'pending_approval' ||
      ['Processing', 'Pending', 'pending_approval'].includes(order.raw_status ?? order.status);
  },

  // Check if order is outstanding
  isOrderOutstanding(order) {
    return !order.finalised && !['c', 'C'].includes(order.status);
  },

  // Get order priority (outstanding orders first)
  getOrderPriority(order) {
    if (this.isOrderOutstanding(order)) {
      return 1; // High priority
    }
    return 2; // Normal priority
  },

  // Get order status history for portal orders (basic implementation)
  async getOrderStatusHistory(orderNumber) {
    try {
      // For portal orders, we don't have detailed status history yet
      // Return empty array to maintain compatibility
      return [];

    } catch (error) {
      console.error('Error fetching order status history:', error);
      // Return empty array instead of throwing to prevent modal from breaking
      return [];
    }
  },

  // Check if order has backorders
  hasBackorders(order) {
    return order.total_backordered_qty > 0;
  },

  // Get backorder line items from cart_items
  getBackorderedItems(order) {
    if (!order.cart_items) return [];
    return order.cart_items.filter(item => 
      item.qty_backordered && item.qty_backordered > 0
    );
  },

  // Get total backorder quantity for display
  getTotalBackorderQty(order) {
    return order.total_backordered_qty || 0;
  },

  // Format fulfillment percentage for display
  formatFulfillment(percentage) {
    if (percentage === null || percentage === undefined) return 'Not synced';
    return `${percentage.toFixed(1)}% fulfilled`;
  },

  // Get fulfillment percentage color
  getFulfillmentColor(percentage) {
    if (!percentage) return 'bg-gray-100 text-gray-800';
    if (percentage >= 100) return 'bg-green-100 text-green-800';
    if (percentage >= 75) return 'bg-blue-100 text-blue-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-orange-100 text-orange-800';
  },

  // Get sync status color
  getSyncStatusColor(syncStatus) {
    const colors = {
      'synced': 'bg-green-100 text-green-800 border-green-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'error': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[syncStatus] || 'bg-gray-100 text-gray-800 border-gray-200';
  },

  // Get sync status display name
  getSyncStatusText(syncStatus) {
    const statusText = {
      'synced': 'Synced',
      'pending': 'Pending Sync',
      'error': 'Sync Error'
    };
    return statusText[syncStatus] || 'Unknown';
  },

  // Check if order needs sync
  needsSync(order) {
    if (!order.last_synced_at) return true;
    if (order.sync_status === 'pending' || order.sync_status === 'error') return true;
    if (order.status === 'Complete' || order.status === 'Cancelled') return false;
    
    // Check if last sync was more than 15 minutes ago
    const lastSync = new Date(order.last_synced_at);
    const now = new Date();
    const diffMinutes = (now - lastSync) / (1000 * 60);
    return diffMinutes > 15;
  },

  // Format last synced time
  formatLastSynced(lastSyncedAt) {
    if (!lastSyncedAt) return 'Never synced';
    
    const syncDate = new Date(lastSyncedAt);
    const now = new Date();
    const diffMinutes = Math.floor((now - syncDate) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  },

  // Check if order has external order number (is synced with ERP)
  hasSyncedWithERP(order) {
    return !!order.external_order_number;
  },

  // Get orders with backorders for summary
  async getOrdersWithBackorders() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_orders_with_backorders', { p_customer_id: user.id });

      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching orders with backorders:', error);
      return [];
    }
  },

  // Manually trigger sync for a specific order (for future use)
  async triggerOrderSync(orderId) {
    try {
      // This would call the sync-portal-orders edge function
      // For now, just a placeholder
      console.log(`Triggering sync for order ${orderId}`);
      // Implementation would go here
      return { success: true };
    } catch (error) {
      console.error('Error triggering order sync:', error);
      return { success: false, error: error.message };
    }
  }
};

export default orderService;
